import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Small uppercase kicker above the title. */
  eyebrow?: ReactNode;
  /** Status pill or badge rendered beside the title. */
  badge?: ReactNode;
  /** Buttons, filters — anything right-aligned. Wraps below the title on narrow viewports. */
  actions?: ReactNode;
  className?: string;
}

/**
 * The page title block. The same three-part markup was inlined into all seven
 * certificate-portal routes with slightly different spacing in each.
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  badge,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && (
          <p className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {eyebrow}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
            {title}
          </h1>
          {badge}
        </div>
        {description && (
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
